import { useEffect, useState } from "react";
import { CalendarDays, MapPin, Plus, Loader2, X, ChevronDown, Download, Clapperboard, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/Badges";
import { resolveMediaUrl } from "@/components/FileUpload";
import { PIPELINE_COLUMNS, STATUS_LABEL, TYPE_LABEL, columnOfStatus } from "@/lib/constants";
import { DocScanButton } from "@/components/DocScanButton";

function daysUntil(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86400000);
}

export function EventsTab({ accountKey }) {
  const { user } = useAuth();
  const myId = String(user?.id || "");
  const [events, setEvents] = useState([]);
  const [shootDays, setShootDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPast, setShowPast] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ title: "", date: "", location: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    api
      .get("/events", { params: { account: accountKey } })
      .then(({ data }) => setEvents(data))
      .catch(() => {})
      .finally(() => setLoading(false));
    api
      .get("/shoot-days", { params: { account: accountKey } })
      .then(({ data }) => setShootDays(data))
      .catch(() => {});
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountKey]);

  const addEvent = async () => {
    if (!form.title.trim() || !form.date) {
      toast.error("Titel und Datum sind Pflicht");
      return;
    }
    setSaving(true);
    try {
      await api.post("/events", { account_key: accountKey, ...form, title: form.title.trim() });
      toast.success("Event angelegt");
      setForm({ title: "", date: "", location: "" });
      setAddOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const upcoming = events.filter((e) => daysUntil(e.date) >= 0);
  const past = events.filter((e) => daysUntil(e.date) < 0);
  const shown = showPast ? past : upcoming;

  return (
    <div className="space-y-3" data-testid="companion-events-tab">
      <div className="flex items-center justify-between">
        <div className="flex gap-1.5">
          <button
            data-testid="events-filter-upcoming"
            onClick={() => setShowPast(false)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full ${!showPast ? "bg-orange-500 text-white" : "bg-white border border-neutral-200 text-neutral-500"}`}
          >
            Anstehend ({upcoming.length})
          </button>
          <button
            data-testid="events-filter-past"
            onClick={() => setShowPast(true)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full ${showPast ? "bg-orange-500 text-white" : "bg-white border border-neutral-200 text-neutral-500"}`}
          >
            Vergangen ({past.length})
          </button>
        </div>
        <button
          data-testid="events-add-btn"
          onClick={() => setAddOpen((o) => !o)}
          className="h-8 w-8 rounded-lg bg-orange-500 flex items-center justify-center text-white"
        >
          {addOpen ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
        </button>
      </div>

      {addOpen && (
        <div className="rounded-2xl bg-white border border-neutral-200 shadow-sm p-3 space-y-2.5">
          <input
            data-testid="events-add-title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Event-Titel"
            className="w-full rounded-lg bg-neutral-100 border border-neutral-200 px-3 py-2.5 text-sm text-neutral-900 placeholder:text-neutral-500 focus:outline-none"
          />
          <div className="flex gap-2">
            <input
              data-testid="events-add-date"
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="flex-1 rounded-lg bg-neutral-100 border border-neutral-200 px-3 py-2.5 text-sm text-neutral-900 focus:outline-none"
            />
            <input
              data-testid="events-add-location"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="Ort (optional)"
              className="flex-1 rounded-lg bg-neutral-100 border border-neutral-200 px-3 py-2.5 text-sm text-neutral-900 placeholder:text-neutral-500 focus:outline-none"
            />
          </div>
          <button
            data-testid="events-add-save"
            onClick={addEvent}
            disabled={saving}
            className="w-full rounded-lg bg-orange-500 py-2.5 text-sm font-semibold text-neutral-900 flex items-center justify-center gap-2"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Event speichern
          </button>
        </div>
      )}

      {(() => {
        const upcomingShoots = shootDays.filter((s) => daysUntil(s.date) >= 0).slice(0, 5);
        if (!upcomingShoots.length) return null;
        return (
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3" data-testid="companion-shootdays-card">
            <p className="text-[11px] font-bold uppercase tracking-wider text-amber-700 flex items-center gap-1.5 mb-2">
              <Clapperboard className="h-3.5 w-3.5" /> Nächste Drehtage
            </p>
            <div className="space-y-2">
              {upcomingShoots.map((s) => {
                const mine = (s.creator_ids || []).map(String).includes(myId);
                return (
                  <div key={s.id} data-testid={`companion-shootday-${s.id}`}
                    className={`rounded-xl px-3 py-2.5 border ${mine ? "bg-amber-500 border-amber-600 text-white" : "bg-white border-amber-200 text-neutral-800"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-bold truncate">🎬 {s.title}</p>
                      {mine && <span className="text-[10px] font-bold bg-white/20 rounded-full px-2 py-0.5 shrink-0">Du bist dabei</span>}
                    </div>
                    <p className={`text-[11px] mt-0.5 ${mine ? "text-amber-100" : "text-neutral-500"}`}>
                      {(() => { try { return new Date(s.date).toLocaleDateString("de-DE", { weekday: "short", day: "numeric", month: "short" }); } catch { return s.date; } })()}
                      {s.time_from ? ` · ${s.time_from}${s.time_to ? `–${s.time_to}` : ""} Uhr` : ""}
                      {s.location ? ` · ${s.location}` : ""}
                      {s.event_title ? ` · 📅 ${s.event_title}` : ""}
                    </p>
                    {s.notes && <p className={`text-[11px] mt-1 line-clamp-2 ${mine ? "text-amber-50" : "text-neutral-400"}`}>{s.notes}</p>}
                    <button
                      data-testid={`companion-shootday-ics-${s.id}`}
                      onClick={async () => {
                        try {
                          const { data } = await api.get(`/shoot-days/${s.id}/ics`, { responseType: "blob" });
                          const url = URL.createObjectURL(new Blob([data], { type: "text/calendar" }));
                          const a = document.createElement("a");
                          a.href = url; a.download = `drehtag-${s.date}.ics`;
                          document.body.appendChild(a); a.click(); a.remove();
                          setTimeout(() => URL.revokeObjectURL(url), 30000);
                          toast.success("Datei öffnen, um den Termin zu speichern 📆");
                        } catch { toast.error("Export fehlgeschlagen"); }
                      }}
                      className={`mt-1.5 text-[11px] font-bold flex items-center gap-1 ${mine ? "text-white underline" : "text-amber-700 underline"}`}
                    >
                      <CalendarDays className="h-3 w-3" /> In meinen Kalender
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {loading && <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-neutral-500" /></div>}
      {!loading && shown.length === 0 && (
        <p className="text-center text-sm text-neutral-500 py-10">Keine Events {showPast ? "in der Vergangenheit" : "anstehend"}.</p>
      )}
      {shown.map((e) => {
        const days = daysUntil(e.date);
        return (
          <div key={e.id} data-testid={`companion-event-${e.id}`} className="rounded-2xl bg-white border border-neutral-200 shadow-sm p-3.5 flex gap-3">
            <div className="w-12 shrink-0 rounded-lg bg-neutral-100 flex flex-col items-center justify-center py-2">
              <div className="text-lg font-bold text-neutral-900 leading-none">{new Date(e.date + "T00:00:00").getDate()}</div>
              <div className="text-[10px] text-neutral-400 uppercase mt-0.5">
                {new Date(e.date + "T00:00:00").toLocaleDateString("de-DE", { month: "short" })}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-neutral-900 leading-snug">{e.title}</h3>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-neutral-400">
                <span className="flex items-center gap-1">
                  <CalendarDays className="h-3 w-3" />
                  {new Date(e.date + "T00:00:00").toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" })}
                </span>
                {e.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {e.location}</span>}
              </div>
              {!showPast && (
                <span className={`inline-block mt-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full ${days <= 7 ? "bg-red-500/15 text-red-400" : "bg-orange-500/15 text-orange-600"}`}>
                  {days === 0 ? "Heute!" : days === 1 ? "Morgen" : `in ${days} Tagen`}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function PipelineTab({ accountKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ title: "", content_type: "reel", hook: "", assigned_creator: "" });
  const [saving, setSaving] = useState(false);
  const [members, setMembers] = useState([]);

  useEffect(() => {
    if ((addOpen || expanded) && members.length === 0)
      api.get("/chat/members").then(({ data }) => setMembers(data)).catch(() => {});
  }, [addOpen, expanded, members.length]);

  const load = () => {
    api
      .get("/content", { params: { account: accountKey } })
      .then(({ data }) => setItems(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountKey]);

  const changeStatus = async (item, status) => {
    try {
      await api.patch(`/content/${item.id}`, { status });
      setItems((prev) => prev.map((c) => (c.id === item.id ? { ...c, status } : c)));
      toast.success(`„${item.title}" → ${STATUS_LABEL[status]}`);
      if (status === "approved" && item.scheduled_date && !item.media_url && !item.video_url)
        toast.warning("⚠️ Kein Bild/Video angehängt — die geplante Instagram-Veröffentlichung wird fehlschlagen", { duration: 8000 });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const changeCreator = async (item, name) => {
    try {
      await api.patch(`/content/${item.id}`, { assigned_creator: name });
      setItems((prev) => prev.map((c) => (c.id === item.id ? { ...c, assigned_creator: name } : c)));
      toast.success(name ? `An ${name} übergeben ✅` : "Zuweisung entfernt");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const submitReview = async (item) => {
    try {
      await api.patch(`/content/${item.id}`, { status: "review" });
      setItems((prev) => prev.map((c) => (c.id === item.id ? { ...c, status: "review" } : c)));
      toast.success("Zur Freigabe eingereicht — die Redaktion wurde benachrichtigt 🔎");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const addIdea = async () => {
    if (!form.title.trim()) {
      toast.error("Bitte einen Titel angeben");
      return;
    }
    setSaving(true);
    try {
      await api.post("/content", { account_key: accountKey, title: form.title.trim(), content_type: form.content_type, hook: form.hook.trim(), assigned_creator: form.assigned_creator, status: "idea" });
      toast.success(form.assigned_creator ? `Idee angelegt — ${form.assigned_creator} wurde benachrichtigt ✅` : "Idee in der Pipeline angelegt ✅");
      setForm({ title: "", content_type: "reel", hook: "", assigned_creator: "" });
      setAddOpen(false);
      setFilter("all");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const counts = PIPELINE_COLUMNS.reduce((acc, c) => {
    acc[c.key] = items.filter((i) => c.statuses.includes(i.status)).length;
    return acc;
  }, {});
  const shown = filter === "all" ? items : items.filter((i) => columnOfStatus(i.status).key === filter);

  return (
    <div className="space-y-4" data-testid="companion-pipeline-tab">
      <div className="mobile-pipeline-summary">
        <div>
          <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-violet-700">Content im Fluss</p>
          <h2 className="mt-1 text-[19px] font-extrabold tracking-[-0.025em] text-slate-950">Von der Idee bis zur Veröffentlichung</h2>
          <p className="mt-1.5 text-[13px] leading-relaxed text-slate-600">Status öffnen, Aufgaben übergeben und Inhalte sicher weiterbewegen.</p>
        </div>
        <span className="mobile-pipeline-total" aria-label={`${items.length} Inhalte insgesamt`}>{items.length}</span>
      </div>

      <button
        data-testid="companion-pipeline-add-btn"
        onClick={() => setAddOpen((o) => !o)}
        className="mobile-pipeline-primary w-full min-h-14 flex items-center justify-center gap-2.5 rounded-[19px] text-white text-[15px] font-extrabold active:scale-[0.985] transition"
      >
        <Plus className="h-5 w-5" /> Neue Content-Idee
      </button>

      {addOpen && (
        <div className="mobile-pipeline-form p-4 space-y-3" data-testid="companion-pipeline-add-form">
          <DocScanButton
            kind="idea"
            label="Foto scannen"
            onExtracted={(f) => setForm((s) => ({
              ...s,
              title: f.title || s.title,
              hook: f.hook || s.hook,
              content_type: ["reel", "story", "post", "carousel"].includes(f.content_type) ? f.content_type : s.content_type,
            }))}
          />
          <input
            data-testid="companion-idea-title"
            value={form.title}
            onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
            placeholder="Titel der Idee *"
            className="mobile-pipeline-field w-full h-12 px-3.5 rounded-2xl text-sm"
          />
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(TYPE_LABEL).map(([k, label]) => (
              <button
                key={k}
                data-testid={`companion-idea-type-${k}`}
                onClick={() => setForm((s) => ({ ...s, content_type: k }))}
                className={`min-h-11 py-2 rounded-[14px] text-xs font-bold border transition ${
                  form.content_type === k ? "bg-violet-600 text-white border-violet-600 shadow-md shadow-violet-500/15" : "bg-white/60 text-slate-600 border-white/80"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <textarea
            data-testid="companion-idea-hook"
            value={form.hook}
            onChange={(e) => setForm((s) => ({ ...s, hook: e.target.value }))}
            placeholder="Idee / Hook (optional)"
            rows={2}
            className="mobile-pipeline-field w-full px-3.5 py-3 rounded-2xl text-sm resize-none"
          />
          <select
            data-testid="companion-idea-creator"
            value={form.assigned_creator}
            onChange={(e) => setForm((s) => ({ ...s, assigned_creator: e.target.value }))}
            className="mobile-pipeline-field w-full h-12 px-3.5 rounded-2xl text-sm"
          >
            <option value="">Niemandem zuweisen</option>
            {members.map((m) => (
              <option key={m.id} value={m.name}>{m.name}{m.role === "admin" ? " (Admin)" : ""}</option>
            ))}
          </select>
          <div className="flex gap-2">
            <button
              data-testid="companion-idea-cancel"
              onClick={() => setAddOpen(false)}
              className="mobile-pipeline-secondary flex-1 min-h-12 rounded-2xl text-sm font-semibold text-slate-600"
            >
              Abbrechen
            </button>
            <button
              data-testid="companion-idea-save"
              onClick={addIdea}
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 min-h-12 rounded-2xl bg-slate-950 text-white text-sm font-bold shadow-lg shadow-slate-900/15 disabled:opacity-50"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Anlegen
            </button>
          </div>
        </div>
      )}

      <div className="mobile-pipeline-filters grid grid-cols-2 gap-2 p-2" data-testid="pipeline-filters">
        <button
          data-testid="pipeline-filter-all"
          onClick={() => setFilter("all")}
          className={`min-h-11 text-xs font-bold px-3 rounded-[14px] transition-all ${filter === "all" ? "bg-slate-950 text-white shadow-md" : "bg-white/55 border border-white/75 text-slate-600"}`}
        >
          Alle ({items.length})
        </button>
        {PIPELINE_COLUMNS.map((c) => (
          <button
            key={c.key}
            data-testid={`pipeline-filter-${c.key}`}
            onClick={() => setFilter(c.key)}
            className={`min-h-11 text-xs font-bold px-3 rounded-[14px] transition-all ${filter === c.key ? "bg-slate-950 text-white shadow-md" : "bg-white/55 border border-white/75 text-slate-600"}`}
          >
            {c.label} ({counts[c.key]})
          </button>
        ))}
      </div>

      {loading && <div className="mobile-pipeline-empty py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div>}
      {!loading && shown.length === 0 && <p className="mobile-pipeline-empty text-center text-sm text-slate-500 py-12">Keine Inhalte in dieser Ansicht.</p>}

      {shown.map((item) => (
        <div key={item.id} data-testid={`companion-pipeline-item-${item.id}`} className={`mobile-pipeline-card ${expanded === item.id ? "is-expanded" : ""}`}>
          <button data-testid={`companion-pipeline-toggle-${item.id}`} className="w-full text-left p-[18px]" onClick={() => setExpanded(expanded === item.id ? null : item.id)}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-slate-400">{TYPE_LABEL[item.content_type] || item.content_type}</p>
                <h3 className="mt-1.5 text-[16px] font-extrabold tracking-[-0.015em] text-slate-950 leading-snug">{item.title}</h3>
              </div>
              <span className="mobile-pipeline-chevron">
                <ChevronDown className={`h-[18px] w-[18px] text-slate-600 shrink-0 transition-transform ${expanded === item.id ? "rotate-180" : ""}`} />
              </span>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <StatusBadge status={item.status} />
              {item.scheduled_date && (
                <span className="mobile-pipeline-chip text-[11px] font-bold text-teal-700">
                  <CalendarDays className="h-3.5 w-3.5" />
                  {new Date(item.scheduled_date).toLocaleDateString("de-DE")}
                </span>
              )}
              {item.assigned_creator && (
                <span className="mobile-pipeline-chip text-[11px] font-bold text-violet-700">{item.assigned_creator}</span>
              )}
            </div>
          </button>
          {expanded === item.id && (
            <div className="mobile-pipeline-details mx-3 mb-3 p-3.5 space-y-3">
              {item.hook && <p className="text-[13px] text-slate-700 leading-relaxed"><span className="font-extrabold">Hook:</span> {item.hook}</p>}
              {item.description && <p className="text-[13px] text-slate-500 leading-relaxed">{item.description}</p>}
              {(item.media_url || item.video_url) && (
                <div className="space-y-2" data-testid={`pipeline-media-${item.id}`}>
                  {item.video_url && (
                    <video src={resolveMediaUrl(item.video_url)} controls playsInline preload="metadata"
                      className="w-full max-h-60 rounded-2xl bg-slate-950" />
                  )}
                  {item.media_url && !item.video_url && (
                    <img src={resolveMediaUrl(item.media_url)} alt={item.title}
                      className="w-full max-h-60 object-contain rounded-2xl bg-white/55" />
                  )}
                  <div className="flex gap-2">
                    {item.video_url && (
                      <a
                        data-testid={`pipeline-download-video-${item.id}`}
                        href={`${resolveMediaUrl(item.video_url)}${resolveMediaUrl(item.video_url).includes("?") ? "&" : "?"}download=1`}
                        className="flex-1 min-h-11 flex items-center justify-center gap-1.5 px-2 rounded-[14px] bg-slate-950 text-white text-xs font-bold"
                      >
                        <Download className="h-3.5 w-3.5" /> Video herunterladen
                      </a>
                    )}
                    {item.media_url && (
                      <a
                        data-testid={`pipeline-download-media-${item.id}`}
                        href={`${resolveMediaUrl(item.media_url)}${resolveMediaUrl(item.media_url).includes("?") ? "&" : "?"}download=1`}
                        className="flex-1 min-h-11 flex items-center justify-center gap-1.5 px-2 rounded-[14px] bg-slate-950 text-white text-xs font-bold"
                      >
                        <Download className="h-3.5 w-3.5" /> {item.video_url ? "Medien herunterladen" : "Herunterladen"}
                      </a>
                    )}
                  </div>
                </div>
              )}
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 block mb-1">Status ändern</label>
                <select
                  data-testid={`pipeline-status-select-${item.id}`}
                  value={columnOfStatus(item.status).key}
                  onChange={(e) => changeStatus(item, PIPELINE_COLUMNS.find((c) => c.key === e.target.value).drop)}
                  className="mobile-pipeline-field w-full min-h-12 rounded-2xl px-3 text-sm text-slate-900 focus:outline-none"
                >
                  {PIPELINE_COLUMNS.map((c) => (
                    <option key={c.key} value={c.key}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-semibold text-neutral-500 shrink-0">Creator</span>
                <select
                  data-testid={`pipeline-creator-select-${item.id}`}
                  value={item.assigned_creator || ""}
                  onChange={(e) => changeCreator(item, e.target.value)}
                  className="mobile-pipeline-field flex-1 h-11 px-3 rounded-[14px] text-xs"
                >
                  <option value="">— Niemand —</option>
                  {members.map((m) => (
                    <option key={m.id} value={m.name}>{m.name}{m.role === "admin" ? " (Admin)" : ""}</option>
                  ))}
                  {item.assigned_creator && !members.some((m) => m.name === item.assigned_creator) && (
                    <option value={item.assigned_creator}>{item.assigned_creator}</option>
                  )}
                </select>
              </div>
              {(item.relevant_groups || []).length > 0 && (
                <p className="text-[11px] text-violet-700 bg-violet-50 border border-violet-200 rounded-lg px-2.5 py-2" data-testid={`pipeline-groups-${item.id}`}>
                  📣 Auch teilen in: {(item.relevant_groups || []).join(", ")}
                </p>
              )}
              {!["review", "approved", "scheduled", "schedule_failed", "published"].includes(item.status) && (
                <button
                  data-testid={`pipeline-submit-review-${item.id}`}
                  onClick={() => submitReview(item)}
                  className="w-full min-h-12 flex items-center justify-center gap-2 rounded-2xl bg-amber-500 active:bg-amber-600 text-white text-xs font-bold transition shadow-md shadow-amber-500/15"
                >
                  <Send className="h-3.5 w-3.5" /> Zur Freigabe einreichen
                </button>
              )}
              {item.status === "review" && (
                <p className="text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2" data-testid={`pipeline-review-hint-${item.id}`}>
                  🔎 Wartet auf Freigabe durch die Redaktion
                </p>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
